import { useId, useState } from "react";

import { ArrowDownward, ArrowUpward, SwapVert } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  SubmissionSort,
  SubmissionSortField,
  SubmissionSortOrder,
} from "@/components/online-reporting/submissionSort";

interface SubmissionSortControlProps {
  sort: SubmissionSort;
  onSortChange: (sort: SubmissionSort) => void;
  /** Shown as a tooltip on the disabled button. */
  disabledReason?: string;
}

const FIELD_LABEL_KEYS: Record<SubmissionSortField, string> = {
  time_updated: "sortTimeUpdated",
  time_submitted: "sortTimeSubmitted",
};

const ORDER_LABEL_KEYS: Record<SubmissionSortOrder, string> = {
  desc: "sortNewestFirst",
  asc: "sortOldestFirst",
};

const SHORT_FIELD_LABEL_KEYS: Record<SubmissionSortField, string> = {
  time_updated: "sortShortTimeUpdated",
  time_submitted: "sortShortTimeSubmitted",
};

const SHORT_ORDER_LABEL_KEYS: Record<SubmissionSortOrder, string> = {
  desc: "sortShortNewest",
  asc: "sortShortOldest",
};

const SubmissionSortControl = ({
  sort,
  onSortChange,
  disabledReason,
}: SubmissionSortControlProps) => {
  const { t } = useTranslation("bulletin/searchbar");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverId = useId();
  const fieldLabelId = useId();
  const orderLabelId = useId();

  const summary = `${t(FIELD_LABEL_KEYS[sort.sortBy])}, ${t(
    ORDER_LABEL_KEYS[sort.sortOrder],
  ).toLowerCase()}`;

  const button = (
    <Button
      className="ml-3"
      sx={{ padding: "0 12px", whiteSpace: "nowrap", flexShrink: 0 }}
      variant="outlined"
      disabled={!!disabledReason}
      aria-haspopup="dialog"
      aria-controls={anchorEl ? popoverId : undefined}
      aria-expanded={!!anchorEl}
      aria-label={`${t("sortBy")}: ${summary}`}
      onClick={(event) => setAnchorEl(event.currentTarget)}
    >
      <SwapVert />
      <span className="ml-2 d-none d-md-flex align-items-center">
        {t(SHORT_FIELD_LABEL_KEYS[sort.sortBy])}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        {t(SHORT_ORDER_LABEL_KEYS[sort.sortOrder])}
      </span>
    </Button>
  );

  return (
    <>
      {disabledReason ? (
        <Tooltip title={disabledReason}>
          <span style={{ display: "inline-flex" }}>{button}</span>
        </Tooltip>
      ) : (
        <Tooltip title={`${t("sortBy")}: ${summary}`}>{button}</Tooltip>
      )}
      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, width: 280 } } }}
      >
        <Typography
          id={fieldLabelId}
          variant="overline"
          color="text.secondary"
          component="div"
        >
          {t("sortBy")}
        </Typography>
        <ToggleButtonGroup
          value={sort.sortBy}
          exclusive
          fullWidth
          orientation="vertical"
          color="primary"
          size="small"
          aria-labelledby={fieldLabelId}
          onChange={(_, sortBy: SubmissionSortField | null) => {
            if (sortBy) onSortChange({ ...sort, sortBy });
          }}
        >
          {(Object.keys(FIELD_LABEL_KEYS) as SubmissionSortField[]).map(
            (field) => (
              <ToggleButton
                key={field}
                value={field}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}
              >
                {t(FIELD_LABEL_KEYS[field])}
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>

        <Box mt={2}>
          <Typography
            id={orderLabelId}
            variant="overline"
            color="text.secondary"
            component="div"
          >
            {t("sortOrder")}
          </Typography>
          <ToggleButtonGroup
            value={sort.sortOrder}
            exclusive
            fullWidth
            color="primary"
            size="small"
            aria-labelledby={orderLabelId}
            onChange={(_, sortOrder: SubmissionSortOrder | null) => {
              if (sortOrder) onSortChange({ ...sort, sortOrder });
            }}
          >
            <ToggleButton value="desc" sx={{ textTransform: "none" }}>
              <ArrowDownward fontSize="small" sx={{ mr: 0.5 }} />
              {t("sortNewestFirst")}
            </ToggleButton>
            <ToggleButton value="asc" sx={{ textTransform: "none" }}>
              <ArrowUpward fontSize="small" sx={{ mr: 0.5 }} />
              {t("sortOldestFirst")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Popover>
    </>
  );
};

export default SubmissionSortControl;
